import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteForm } from "@/features/qualifications/hooks/use-qualification-forms";
import type { QualificationForm } from "@/features/qualifications/types";
import { useTranslation } from "@/features/utils/i18n";
import {
  DotsThreeVerticalIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

export const Action = ({ form }: { form: QualificationForm }) => {
  const { t } = useTranslation("qualifications");
  const navigate = useNavigate();
  const { company: clientId } = useParams<{ company: string }>();
  const deleteForm = useDeleteForm();

  const handleEdit = () => {
    navigate(`/${clientId}/settings/qualification-forms/${form.id}/edit`);
  };

  const handleDelete = async () => {
    try {
      await deleteForm.mutateAsync(form.id);
      toast.success(t("forms.messages.deleted"));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("forms.messages.deleteError"),
      );
    }
  };

  return (
    <div className="flex items-center justify-end gap-2 text-right">
      <Button variant="ghost" size="sm" onClick={handleEdit}>
        <EyeIcon className="size-4" weight="fill" />
        {t("forms.actions.view")}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            aria-label={t("forms.actions.more")}
          >
            <DotsThreeVerticalIcon className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleEdit}>
            <PencilIcon className="mr-2 size-4" />
            {t("forms.actions.edit")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {/* The confirmation dialog rather than `window.confirm`, as the
              other lists' row menus do. */}
          <DropdownMenuItem className="p-0">
            <Button
              variant="ghost"
              onClick={handleDelete}
              className="w-full justify-start text-danger hover:text-danger"
              confirm={t("forms.messages.deleteConfirm", {
                name: form.name || t("forms.untitled"),
              })}
            >
              <TrashIcon className="mr-2 size-4" />
              {t("forms.actions.delete")}
            </Button>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
